import { ENVIRONMENT_CONTEXT, ENVIRONMENT_NAME } from 'config/env';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserQuery } from '../actions/api/user';
import { clearActiveRole, getActiveRole, setActiveRole as persistActiveRole } from '../api/localStorage';
import { deriveActiveRole } from '../domain/user';

const ActiveRoleContext = createContext({ activeRole: null, setActiveRole: () => {} });

const getOrganizationUrlParam = () => new URL(globalThis.location.toString()).searchParams.get('organization');

const updateDocumentTitle = role => {
  const productName = document.title.split('|')[0].trim();
  document.title =
    [productName, role?.organizationDetails?.name, ENVIRONMENT_NAME, ENVIRONMENT_CONTEXT].filter(Boolean).join(' | ') ||
    undefined;
};

export const ActiveRoleProvider = ({ children }) => {
  const { data: user } = useUserQuery();
  const [activeRole, setActiveRole] = useState(null);
  const activeRoleRef = useRef(activeRole);
  const hasLoadedUser = useRef(false);

  const applyActiveRole = useCallback(role => {
    activeRoleRef.current = role;
    if (role) {
      persistActiveRole(role);
    } else {
      clearActiveRole();
    }
    setActiveRole(role);
    updateDocumentTitle(role);
  }, []);

  const setActiveRoleCallback = useCallback(role => applyActiveRole(role), [applyActiveRole]);

  useEffect(() => {
    if (!user) {
      if (hasLoadedUser.current) {
        hasLoadedUser.current = false;
        applyActiveRole(null);
      }
      return;
    }
    if (!user.roles) {
      return;
    }
    const isInitialDerivation = !hasLoadedUser.current;
    hasLoadedUser.current = true;
    // The ?organization= URL param only seeds the initial role on login; afterward the
    // user's current (possibly manually switched) role is preserved when still available.
    const orgParam = isInitialDerivation ? getOrganizationUrlParam() : null;
    const preferredRole = isInitialDerivation ? getActiveRole() : activeRoleRef.current;
    applyActiveRole(deriveActiveRole(user, preferredRole, orgParam));
  }, [user, applyActiveRole]);

  const value = useMemo(
    () => ({ activeRole, setActiveRole: setActiveRoleCallback }),
    [activeRole, setActiveRoleCallback]
  );

  return <ActiveRoleContext.Provider value={value}>{children}</ActiveRoleContext.Provider>;
};

export const useActiveRole = () => useContext(ActiveRoleContext);
